import { defineComponent as _defineComponent } from 'vue'
import { now } from '@/utils/datetime';
import SdsSection2Form from '@/views/dashboard/sdsDrawer/forms/SdsSection2Form.vue';
import type { SdsSection2WithIndex } from '@/views/components/views/dashboard/SdsTableSection2AdminGHS.vue';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import { readUserLocale } from '@/store/main/getters';
import { store } from '@/store/index';
import { defineMessages } from '@/lib/i18n';

import { ref, computed, PropType, onMounted } from 'vue';
import { SdsSection2, SdsSection2Hazard } from '@/interfaces/model/admin/sds';
import { getSection2GHSCategoryList } from '@/lib/formItem';
import {
  useMSdsSection2,
  UseMSdsSection2Return,
} from '@/composables/useMSdsSection2';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsTableSection2List',
  props: {
  items: {
    type: Array as PropType<Array<SdsSection2>>,
    required: true,
  },
  sdsSection2List: {
    type: Array as PropType<Array<SdsSection2WithIndex>>,
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  classificationSystemId: {
    type: String,
    required: true,
  },
  materialName: {
    type: String,
    default: null,
  },
  disableAdd: {
    type: Boolean,
    default: false,
  },
},
  emits: ["update:isValid", "update:sdsSection2List"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:isValid', data: boolean): void;
  (e: 'update:sdsSection2List', data: SdsSection2[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);
const locale = computed(() => readUserLocale(store));
const textProperty = locale.value === 'ja-JP' ? 'name' : 'name_en';

const m = defineMessages({
  bulkEditMenu: '一括編集メニュー',
  physicochemicalHazard: '物理化学的危険性',
  nonClassified: '区分外にする',
  returnToHyphen: 'ハイフンに戻す',
  healthHazard: '健康に対する有害性',
  environmentalHazard: '環境に対する有害性等',
  allItems: '全ての項目',
  item: '項目',
  toxicity: '毒性',
  cancelDeleteFlagWarning:
    '「削除フラグを取り消す」をクリックすると、データの削除フラグが取り消されます。',
  deleteFlagWarning:
    '「削除する」をクリックすると、データがDBから削除されます。この操作は戻すことができません',
  addSection2: '危険有害性の要約情報を追加',
  editSection2: '危険有害性の要約情報を変更',
});



const isCollapsed = ref<boolean>(false);
const selectedItem = ref<SdsSection2WithIndex>({});
const isAdd = computed<boolean>(() => selectedItem.value.id === undefined);
const {
  categories: mSdsSection2Categories,
  items: mSdsSection2Items,
  hazards: mSdsSection2Hazards,
  load: loadMSection2,
  reserved_ids,
}: UseMSdsSection2Return = useMSdsSection2();

const showDeleteModal = ref<boolean>();
const toggleDeleteModal = () => {
  showDeleteModal.value = !showDeleteModal.value;
  if (!showDeleteModal.value) {
    selectedItem.value = {};
  }
};
const toggleDeleteSection2 = (): void => {
  const { section2_list_index } = selectedItem.value;
  const data = props.sdsSection2List;
  /// bug think about id 0....
  data[section2_list_index].deleted_at = data[section2_list_index].deleted_at
    ? null
    : now().toISO();
  emits('update:sdsSection2List', structuredClone(data));
};

const isValidUpdatedSection2 = ref<boolean>(false);
const showEditModal = ref<boolean>();
const toggleEditModal = () => {
  showEditModal.value = !showEditModal.value;
  if (!showEditModal.value) {
    selectedItem.value = {};
  }
};



const upsertSdsSection2List = () => {
  const sdsSection2List: Array<SdsSection2> = props.sdsSection2List;
  const { section2_list_index, category_id, ...item } = selectedItem.value;
  const newData = {
    ...item,
    classification_system_id: props.classificationSystemId,
    material_name: props.materialName,
  };
  if (isAdd.value) {
    // add
    category_id.forEach((cid) => {
      const existingIndex = sdsSection2List.findIndex((s2l) => {
        return (
          s2l.classification_system_id === props.classificationSystemId &&
          String(s2l.material_name) === String(props.materialName) &&
          String(s2l.item_id) === String(item.item_id) &&
          [String(cid), 0].indexOf(String(s2l.category_id)) !== -1
        );
      });
      if (existingIndex !== -1) {
        sdsSection2List[existingIndex] = {
          ...sdsSection2List[existingIndex],
          ...newData,
          category_id: cid,
          deleted_at: null, // recover exsiting
        };
      } else {
        sdsSection2List.push({
          id: 0,
          sds_id: '',
          sds_product_id: 0,
          created_at: now().toISO(),
          updated_at: now().toISO(),
          deleted_at: null,
          sds_section2_hazard_list: [],
          ...newData,
          category_id: cid,
        });
      }
    });
  } else {
    // edit
    sdsSection2List[section2_list_index] = {
      ...newData,
      category_id,
      deleted_at: null, // recover exsiting
    };
  }
  emits('update:sdsSection2List', structuredClone(sdsSection2List));
};

const headers = [
  { text: $t(m.item), value: 'itemId', sortable: false, width: '30%' },
  {
    text: $t(sharedMessages.category),
    value: 'categoryId',
    sortable: false,
    width: '30%',
  },
  { text: $t(m.toxicity), value: 'hazardId', sortable: false, width: '20%' },
  { text: '', value: 'action', sortable: false, align: 'end' },
];

const getCategoryList = (item_id) =>
  getSection2GHSCategoryList(mSdsSection2Categories.value).filter(
    (x) => x.itemId == item_id
  );

const addGHSAsNonClassified = (): void => {
  const updated = structuredClone(props.sdsSection2List);

  props.items.forEach((section2: SdsSection2) => {
    const idx = section2.section2_list_index;
    if (idx !== undefined) {
      const categoryList = getCategoryList(section2.item_id);
      const notApplicable = categoryList.find((c) =>
        c.text.includes('非該当 ; Not applicable')
      );
      updated[idx].category_id = notApplicable
        ? notApplicable.value
        : reserved_ids.CATEGORY_NOT_CATEGORIZABLE;
      updated[idx].sds_section2_hazard_list.forEach(
        (hazard: SdsSection2Hazard) => {
          hazard.hazard_id = reserved_ids.HAZARD_DEFAULT;
        }
      );
    }
  });

  emits('update:sdsSection2List', updated);
};

// 物理化学的危険性
const addGHSPhysicochemicalHazardAsNonClassified = (): void => {
  const updated = structuredClone(props.sdsSection2List);

  props.items.forEach((section2: SdsSection2) => {
    if (reserved_ids.ITEM_PHYSICOCHEMICALS.includes(section2.item_id)) {
      const idx = section2.section2_list_index;
      if (idx !== undefined) {
        const categoryList = getCategoryList(section2.item_id);
        const notApplicable = categoryList.find((c) =>
          c.text.includes('非該当 ; Not applicable')
        );
        updated[idx].category_id = notApplicable
          ? notApplicable.value
          : reserved_ids.CATEGORY_NOT_CATEGORIZABLE;
        updated[idx].sds_section2_hazard_list.forEach(
          (hazard: SdsSection2Hazard) => {
            hazard.hazard_id = reserved_ids.HAZARD_DEFAULT;
          }
        );
      }
    }
  });

  emits('update:sdsSection2List', updated);
};

// 健康に対する有害性
const addGHSHealthHazardAsNonClassified = (): void => {
  const updated = structuredClone(props.sdsSection2List);

  props.items.forEach((section2: SdsSection2) => {
    if (reserved_ids.ITEM_HEALTH_HAZARDS.includes(section2.item_id)) {
      const idx = section2.section2_list_index;
      if (idx !== undefined) {
        const categoryList = getCategoryList(section2.item_id);
        const notApplicable = categoryList.find((c) =>
          c.text.includes('非該当 ; Not applicable')
        );
        updated[idx].category_id = notApplicable
          ? notApplicable.value
          : reserved_ids.CATEGORY_NOT_CATEGORIZABLE;
        updated[idx].sds_section2_hazard_list.forEach((hazard) => {
          hazard.hazard_id = reserved_ids.HAZARD_DEFAULT;
        });
      }
    }
  });

  emits('update:sdsSection2List', updated);
};

// 環境に対する有害性等
const addGHSEnvironmentalHazardAsNonClassified = (): void => {
  const updated = structuredClone(props.sdsSection2List);

  props.items.forEach((section2: SdsSection2) => {
    if (reserved_ids.ITEM_ENVIRONMENTAL_HAZARDS.includes(section2.item_id)) {
      const idx = section2.section2_list_index;
      if (idx !== undefined) {
        const categoryList = getCategoryList(section2.item_id);
        const notApplicable = categoryList.find((c) =>
          c.text.includes('非該当 ; Not applicable')
        );
        updated[idx].category_id = notApplicable
          ? notApplicable.value
          : reserved_ids.CATEGORY_NOT_CATEGORIZABLE;
        updated[idx].sds_section2_hazard_list.forEach((hazard) => {
          hazard.hazard_id = reserved_ids.HAZARD_DEFAULT;
        });
      }
    }
  });

  emits('update:sdsSection2List', updated);
};

const addGHSAsHyphen = (): void => {
  const updated = structuredClone(props.sdsSection2List);

  props.items.forEach((section2: SdsSection2) => {
    const idx = section2.section2_list_index;
    if (idx !== undefined) {
      const categoryList = getCategoryList(section2.item_id);
      const notStated = categoryList.find((c) =>
        c.text.includes('該非記載なし ; Not stated')
      );
      updated[idx].category_id = notStated
        ? notStated.value
        : reserved_ids.CATEGORY_DEFAULT;
      updated[idx].sds_section2_hazard_list.forEach((hazard) => {
        hazard.hazard_id = reserved_ids.HAZARD_DEFAULT;
      });
    }
  });

  emits('update:sdsSection2List', updated);
};

const addGHSPhysicochemicalHazardAsHyphen = (): void => {
  const section2List = structuredClone(props.sdsSection2List);
  props.items.forEach((item: { section2_list_index: number }) => {
    const idx = item.section2_list_index;
    const section2 = section2List[idx];
    if (!section2) return;

    if (reserved_ids.ITEM_PHYSICOCHEMICALS.includes(section2.item_id)) {
      const categoryList = getCategoryList(section2.item_id);
      const notStated = categoryList.find((c) =>
        c.text.includes('該非記載なし ; Not stated')
      );
      section2.category_id = notStated
        ? notStated.value
        : reserved_ids.CATEGORY_DEFAULT;
      section2.sds_section2_hazard_list.forEach((hazard: SdsSection2Hazard) => {
        hazard.hazard_id = reserved_ids.HAZARD_DEFAULT;
      });
    }
  });

  emits('update:sdsSection2List', section2List);
};
const addGHSHealthHazardAsHyphen = (): void => {
  const updated = structuredClone(props.sdsSection2List);

  props.items.forEach((section2: SdsSection2) => {
    if (reserved_ids.ITEM_HEALTH_HAZARDS.includes(section2.item_id)) {
      const idx = section2.section2_list_index;
      if (idx !== undefined) {
        const categoryList = getCategoryList(section2.item_id);
        const notStated = categoryList.find((c) =>
          c.text.includes('該非記載なし ; Not stated')
        );
        updated[idx].category_id = notStated
          ? notStated.value
          : reserved_ids.CATEGORY_DEFAULT;
        updated[idx].sds_section2_hazard_list.forEach((hazard) => {
          hazard.hazard_id = reserved_ids.HAZARD_DEFAULT;
        });
      }
    }
  });

  emits('update:sdsSection2List', updated);
};

const addGHSEnvironmentalHazardAsHyphen = (): void => {
  const updated = structuredClone(props.sdsSection2List);

  props.items.forEach((section2: SdsSection2) => {
    if (reserved_ids.ITEM_ENVIRONMENTAL_HAZARDS.includes(section2.item_id)) {
      const idx = section2.section2_list_index;
      if (idx !== undefined) {
        const categoryList = getCategoryList(section2.item_id);
        const notStated = categoryList.find((c) =>
          c.text.includes('該非記載なし ; Not stated')
        );
        updated[idx].category_id = notStated
          ? notStated.value
          : reserved_ids.CATEGORY_DEFAULT;
        updated[idx].sds_section2_hazard_list.forEach((hazard) => {
          hazard.hazard_id = reserved_ids.HAZARD_DEFAULT;
        });
      }
    }
  });

  emits('update:sdsSection2List', updated);
};

onMounted(async () => {
  await loadMSection2();
});

return { __sfc: true,$t, locale, textProperty, m, props, isCollapsed, selectedItem, isAdd, mSdsSection2Categories, mSdsSection2Items, mSdsSection2Hazards, loadMSection2, reserved_ids, showDeleteModal, toggleDeleteModal, toggleDeleteSection2, isValidUpdatedSection2, showEditModal, toggleEditModal, emits, upsertSdsSection2List, headers, getCategoryList, addGHSAsNonClassified, addGHSPhysicochemicalHazardAsNonClassified, addGHSHealthHazardAsNonClassified, addGHSEnvironmentalHazardAsNonClassified, addGHSAsHyphen, addGHSPhysicochemicalHazardAsHyphen, addGHSHealthHazardAsHyphen, addGHSEnvironmentalHazardAsHyphen, SdsSection2Form, sharedMessages }
}

})